import {
  WHATSAPP_IMAGE_MAX_BYTES,
  isWhatsAppImageMimeAllowed,
  isWhatsAppProfilePictureLargeEnough,
} from "@multi-chat/shared";

export type WhatsAppProfilePictureSelectionIssue =
  | "invalidType"
  | "tooLarge"
  | "tooSmall"
  | "undecodable";

export function classifyWhatsAppProfilePictureSelection(input: {
  mimeType: string;
  byteSize: number;
  width: number | null;
  height: number | null;
}): WhatsAppProfilePictureSelectionIssue | null {
  if (!isWhatsAppImageMimeAllowed(input.mimeType)) {
    return "invalidType";
  }
  if (input.byteSize > WHATSAPP_IMAGE_MAX_BYTES) {
    return "tooLarge";
  }
  if (input.width == null || input.height == null || input.width < 1 || input.height < 1) {
    return "undecodable";
  }
  if (!isWhatsAppProfilePictureLargeEnough(input.width, input.height)) {
    return "tooSmall";
  }
  return null;
}

export async function readImageFileDimensions(
  file: File,
): Promise<{ width: number; height: number } | null> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file);
      const size = { width: bitmap.width, height: bitmap.height };
      bitmap.close();
      if (size.width > 0 && size.height > 0) {
        return size;
      }
    } catch {
      // Fall through to HTMLImageElement.
    }
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    return await new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        resolve(
          image.naturalWidth > 0 && image.naturalHeight > 0
            ? { width: image.naturalWidth, height: image.naturalHeight }
            : null,
        );
      };
      image.onerror = () => resolve(null);
      image.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
